import { useNavigate } from "react-router-dom";
import {
  BreadCrumbs,
  Container,
  DataFiled,
  Heading,
  HorizontalLine,
  NoshowContentContainer,
  NoshowHistoryContent,
  Section,
  UpperSection,
} from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import { useEffect, useState } from "react";
import { TNoShowDetailInfo } from "./types";
import { NoshowDetailService } from "./service";
import { getDay, getKorDate, getKorDateForRecurring, getTimeString } from "utils/date";
import { useRecoilValue } from "recoil";
import ToolTip from "uis/new-tootip";
import { member, workspace } from "recoil/recoilState";
import RepeatCancelModal from "../modals/repeat-cancel-modal";
import { useToast } from "hook/useToast";
import { UserRole } from "utils/role";
import { AmplitudeService } from "utils/amplitude.service";
import { TSearchPeriod } from "../NoshowReservationManagementTab/types";

const NoshowReservationDetail = ({ id, period }: { id: string; period: TSearchPeriod }) => {
  const navigate = useNavigate();
  const services = new NoshowDetailService();
  const [rsvDetail, setRsvDetail] = useState<TNoShowDetailInfo>();
  const [hasVisitors, setHasVisitors] = useState<boolean>(false);
  const memberInfo = useRecoilValue(member);
  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
  const { showToast } = useToast();
  const [openCancelRequestModal, setOpenCancelRequestModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const workspaceInfo = useRecoilValue(workspace);
  const amplitudeService = new AmplitudeService();
  const [isCanceling, setIsCanceling] = useState<boolean>(false);

  const onNavigateNoshowList = () => {
    navigate("/meeting/운영%2F예약%20정책%20관리/노쇼%20정책%20관리", {
      state: {
        period: period,
        tab: "노쇼 예약 관리",
      },
    });
  };

  const getAttendeesString = () => {
    if (memberInfo && memberInfo.role > UserRole.SuperAdmin && rsvDetail?.isPrivate) {
      return "비공개";
    } else {
      const attendees = rsvDetail?.reservationAttendees.map((attendee, index) => (
        <span key={index}>
          {!attendee.isVisitor ? `${attendee.member.nickname} (${attendee.member.email})` : `${attendee.visitorEmail}*`}
          {index !== rsvDetail.reservationAttendees.length - 1 ? <br /> : ""}
        </span>
      ));
      return attendees?.length ? attendees : "-";
    }
  };

  const setNoshowDetailInfo = () => {
    setLoading(true);
    services.getNoshowDetailInfo(id, period.startDate, period.endDate).then((res) => {
      if (res) {
        amplitudeService.trackingGetNoshowDetailInfo(
          workspaceInfo?.id ?? "",
          workspaceInfo?.name ?? "",
          res.instanceId,
          res.recurrenceData ? true : false,
          res.noshowData?.noshowNumInPeriod ?? 1
        );

        setRsvDetail(res);
        setHasVisitors(services.getHasVisitors(res));
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    setNoshowDetailInfo();
  }, []);

  return (
    <Container>
      {/* Heading입니다.  */}
      <UpperSection>
        <BreadCrumbs>
          <UI.ButtonGroups.Text title="노쇼 예약 관리" type="secondary" size="small" onClick={onNavigateNoshowList} />
          <UI.Icon name="arrowRight" size={12} color="var(--alias-color-label-neutral)" />
          {!loading && (
            <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-neutral)">
              {services.getTitle(memberInfo?.role, rsvDetail)}
            </UI.Typography>
          )}
          {loading && <div className="skeleton" style={{ width: 100, height: 24 }}></div>}
        </BreadCrumbs>
        <Heading>
          <div className="title">
            {rsvDetail?.recurrenceData && <UI.Tag icon={"change"} showIcon color="skyblue" label="반복 예약" />}
            {!loading && (
              <UI.Typography type="title03" fontStyle="bold" color="var(--alias-color-label-neutral)">
                {services.getTitle(memberInfo?.role, rsvDetail)}
              </UI.Typography>
            )}
            {loading && <div className="skeleton" style={{ width: 250, height: 32 }}></div>}
          </div>
          {services.getIsButtonActive(rsvDetail?.cancelability ?? false, memberInfo?.role) && (
            <div className="btns">
              <UI.Button
                title="취소 요청 보내기"
                size="medium"
                primary={false}
                prefixIcon="mail"
                showPrefixIcon
                disabled={isCanceling}
                onClick={() => {
                  if (isCanceling) return;
                  if (
                    rsvDetail?.recurrenceData?.terminatedAt
                      ? new Date(rsvDetail?.recurrenceData?.terminatedAt) < new Date()
                      : false
                  ) {
                    showToast(`"${rsvDetail?.title}" 반복 예약에 취소 가능한 잔여 예약이 없습니다.`, "error");
                    return;
                  }
                  setOpenCancelRequestModal(true);
                }}
              />
              <UI.Button
                title="반복 예약 취소하기"
                size="medium"
                prefixIcon="removeCircle"
                showPrefixIcon
                disabled={isCanceling}
                onClick={() => {
                  if (isCanceling) return;
                  if (
                    rsvDetail?.recurrenceData?.terminatedAt
                      ? new Date(rsvDetail?.recurrenceData?.terminatedAt) < new Date()
                      : false
                  ) {
                    showToast(`"${rsvDetail?.title}" 반복 예약에 취소 가능한 잔여 예약이 없습니다.`, "error");
                    return;
                  }
                  setOpenCancelModal(true);
                }}
              />
            </div>
          )}
        </Heading>
      </UpperSection>
      {/* 노쇼 상세 정보 섹션입니다. */}
      <Section>
        {/* 최근 노쇼 예약 */}
        <NoshowContentContainer>
          <div className="title">
            <UI.Icon name="removeCircle" color="var(--alias-color-label-neutral)" size={20} />
            <UI.Typography type="headline01" fontStyle="bold" color="var(--alias-color-label-neutral)">
              최근 노쇼 예약
            </UI.Typography>
          </div>
          <div className="content-container">
            <UI.Typography type="heading02" color="var(--alias-color-label-normal)">
              {!loading && services.getTitle(memberInfo?.role, rsvDetail)}
              {loading && <div className="skeleton" style={{ width: 140, height: 28 }}></div>}
            </UI.Typography>
            {!loading ? (
              <div className="content">
                <div className="row-section">
                  <div className="people">
                    <DataFiled>
                      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                        예약자
                      </UI.Typography>
                      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                        {services.getCreatorString(memberInfo?.role, rsvDetail)}
                      </UI.Typography>
                    </DataFiled>
                    <DataFiled>
                      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                        참석자
                      </UI.Typography>
                      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                        {getAttendeesString()}
                      </UI.Typography>
                    </DataFiled>
                    {hasVisitors && (
                      <UI.Typography type="label01" color="var(--alias-color-label-alternative)">
                        *워크스페이스 외부 인원
                      </UI.Typography>
                    )}
                  </div>
                  <div className="divider"></div>
                  <div className="info">
                    <DataFiled>
                      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                        예약 일자
                      </UI.Typography>
                      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                        {getKorDate(rsvDetail?.rsvStartTime ?? "").replaceAll(".", ". ")} (
                        {getDay(rsvDetail?.rsvStartTime ?? "")})
                      </UI.Typography>
                    </DataFiled>
                    <DataFiled>
                      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                        예약 시간
                      </UI.Typography>
                      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                        {getTimeString(rsvDetail?.rsvStartTime ?? "")} - {getTimeString(rsvDetail?.rsvEndTime ?? "")}
                      </UI.Typography>
                    </DataFiled>
                    <DataFiled>
                      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                        회의실
                      </UI.Typography>
                      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                        {[rsvDetail?.buildingName, rsvDetail?.floorName, rsvDetail?.meetingRoomName]
                          .filter((item) => item)
                          .join(" - ")}
                      </UI.Typography>
                    </DataFiled>
                  </div>
                </div>
                <HorizontalLine></HorizontalLine>
                <DataFiled>
                  <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                    예약 메모
                  </UI.Typography>
                  <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                    {services.getMemo(memberInfo?.role, rsvDetail)}
                  </UI.Typography>
                </DataFiled>
              </div>
            ) : (
              <div className="skeleton" style={{ width: "100%", height: 300 }}></div>
            )}
          </div>
        </NoshowContentContainer>
        {/* 반복 예약 */}
        {rsvDetail?.recurrenceData && (
          <NoshowContentContainer>
            <div className="title">
              <UI.Icon name="change" color="var(--alias-color-label-neutral)" size={20} />
              <UI.Typography type="headline01" fontStyle="bold" color="var(--alias-color-label-neutral)">
                반복 예약
              </UI.Typography>
            </div>
            <div className="content-container">
              <div className="content">
                <div className="row-section">
                  <div className="info">
                    <DataFiled>
                      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                        반복 시작 일자
                      </UI.Typography>
                      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                        {rsvDetail.recurrenceData.startAt
                          ? getKorDate(rsvDetail?.recurrenceData?.startAt ?? "").replaceAll(".", ". ")
                          : "N/D"}
                      </UI.Typography>
                    </DataFiled>
                    <DataFiled>
                      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                        반복 종료 일자
                      </UI.Typography>
                      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                        {rsvDetail.recurrenceData.terminatedAt
                          ? getKorDateForRecurring(rsvDetail?.recurrenceData?.terminatedAt).replaceAll(".", ". ")
                          : "무기한 반복"}
                      </UI.Typography>
                    </DataFiled>
                  </div>
                  <div className="divider"></div>
                  <div className="info">
                    <DataFiled>
                      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                        반복 주기
                      </UI.Typography>
                      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                        {rsvDetail?.recurrenceData?.rruleStr}
                      </UI.Typography>
                    </DataFiled>
                    <DataFiled>
                      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                        예약 시간
                      </UI.Typography>
                      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                        {getTimeString(rsvDetail?.recurrenceData?.rsvStartTime ?? "")} -
                        {getTimeString(rsvDetail?.recurrenceData?.rsvEndTime ?? "")}
                      </UI.Typography>
                    </DataFiled>
                  </div>
                </div>
              </div>
            </div>
          </NoshowContentContainer>
        )}
        {/* 노쇼 기록 */}
        {rsvDetail?.noshowData && (
          <NoshowContentContainer>
            <div className="title">
              <UI.Icon name="clock" color="var(--alias-color-label-neutral)" size={20} />
              <UI.Typography type="headline01" fontStyle="bold" color="var(--alias-color-label-neutral)">
                노쇼 기록
              </UI.Typography>
            </div>
            <NoshowHistoryContent>
              <div className="content-container">
                <div className="content">
                  <div className="row-section">
                    <div className="info">
                      <DataFiled>
                        <UI.Typography
                          type="label01"
                          fontStyle="bold"
                          color="var(--alias-color-label-alternative)"
                          style={{
                            display: "flex",
                            gap: 4,
                          }}
                        >
                          <p>
                            조회 기간({getKorDate(period.startDate.toDateString()).replaceAll(".", ". ")} ~{" "}
                            {getKorDate(period.endDate.toDateString()).replaceAll(".", ". ")}) 내 발생 노쇼 횟수
                          </p>
                          <ToolTip
                            icon={<UI.Icon name="info" size={16} color="var(--alias-color-label-assistive)" />}
                            text={"노쇼 예약 목록 조회 시 설정한 기간 내에 발생한 노쇼 횟수입니다."}
                            position={{
                              top: -24,
                              left: -342,
                            }}
                          />
                        </UI.Typography>
                        <UI.Typography type="heading02" fontStyle="regular" color="var(--alias-color-label-normal)">
                          {rsvDetail?.noshowData?.noshowNumInPeriod ?? 1}회
                        </UI.Typography>
                      </DataFiled>
                    </div>
                    <div className="divider"></div>
                    <div className="info">
                      <DataFiled>
                        <UI.Typography
                          type="label01"
                          fontStyle="bold"
                          color="var(--alias-color-label-alternative)"
                          style={{
                            display: "flex",
                            gap: 4,
                          }}
                        >
                          <p>전체 누적 노쇼 횟수</p>
                          <ToolTip
                            icon={<UI.Icon name="info" size={16} color="var(--alias-color-label-assistive)" />}
                            text={"반복 예약 시작 일자부터 당일까지 발생한 모든 노쇼 횟수의 총합입니다."}
                            position={{
                              top: -24,
                              left: -112,
                            }}
                          />
                        </UI.Typography>
                        <UI.Typography type="heading02" fontStyle="regular" color="var(--alias-color-label-normal)">
                          {rsvDetail?.noshowData?.totalNoshowNum}회
                        </UI.Typography>
                      </DataFiled>
                    </div>
                  </div>
                </div>
              </div>
              <div className="content-container">
                <div className="history-content">
                  <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                    노쇼 발생 일시 목록
                  </UI.Typography>
                  {rsvDetail?.noshowData?.noshowDateList.map((history, index) => (
                    <>
                      <UI.Typography
                        key={index}
                        type="label01"
                        fontStyle="regular"
                        color="var(--alias-color-label-normal)"
                      >
                        • {getKorDate(history.rsvStartTime).replaceAll(".", ". ")} ({getDay(history.rsvStartTime)}){" "}
                        {getTimeString(history.rsvStartTime)} - {getTimeString(history.rsvEndTime ?? "")}
                      </UI.Typography>
                      {index !== rsvDetail.noshowData?.noshowDateList.length - 1 && <div className="line"></div>}
                    </>
                  ))}
                </div>
              </div>
            </NoshowHistoryContent>
          </NoshowContentContainer>
        )}
      </Section>
      <RepeatCancelModal
        openModal={openCancelModal}
        closeModal={() => setOpenCancelModal(false)}
        reservationInfo={rsvDetail ?? null}
        onHandleCancel={async () => {
          setIsCanceling(true);
          const res = await services.cancelNoshow(rsvDetail?.instanceId ?? "");
          if (res) {
            amplitudeService.trackingCancelNoshow(
              workspaceInfo?.id ?? "",
              workspaceInfo?.name ?? "",
              rsvDetail?.instanceId ?? "",
              rsvDetail?.noshowData?.noshowNumInPeriod ?? 1,
              "DETAIL",
              rsvDetail?.recurrenceData?.startAt ?? "",
              rsvDetail?.recurrenceData?.terminatedAt ?? "",
              rsvDetail?.recurrenceData?.rruleStr ?? "",
              rsvDetail?.noshowData?.totalNoshowNum ?? 1
            );

            if (rsvDetail?.googleEventId) {
              //* INFO: Google 회의실일 경우, 8초 지연
              await setTimeout(() => {}, 8000);
            }

            showToast(`"${rsvDetail?.title}" 반복 예약이 취소되었습니다.`, "success");
            setNoshowDetailInfo();
            setIsCanceling(false);
            setOpenCancelModal(false);
          } else {
            showToast(`"${rsvDetail?.title}"반복 예약 취소에 실패했습니다.`, "error");
            setIsCanceling(false);
            setOpenCancelModal(false);
          }
        }}
        type={"cancel"}
      />
      <RepeatCancelModal
        openModal={openCancelRequestModal}
        closeModal={() => setOpenCancelRequestModal(false)}
        reservationInfo={rsvDetail ?? null}
        onHandleCancel={async () => {
          const res = await services.requestCancelNoshow(rsvDetail?.instanceId ?? "");
          if (res) {
            amplitudeService.trackingRequestCancelNoshow(
              workspaceInfo?.id ?? "",
              workspaceInfo?.name ?? "",
              rsvDetail?.instanceId ?? "",
              rsvDetail?.noshowData?.noshowNumInPeriod ?? 0,
              "DETAIL",
              rsvDetail?.recurrenceData?.startAt ?? "",
              rsvDetail?.recurrenceData?.terminatedAt ?? "",
              rsvDetail?.recurrenceData?.rruleStr ?? "",
              rsvDetail?.noshowData?.totalNoshowNum ?? 1
            );
            showToast(`"${rsvDetail?.title}" 반복 예약의 취소 요청 알림을 전송했습니다.`, "success");
            setOpenCancelRequestModal(false);
          } else showToast(`"${rsvDetail?.title}"반복 예약 취소 요청 알림 전송에 실패했습니다..`, "error");
        }}
        type={"cancelRequest"}
      />
    </Container>
  );
};
export default NoshowReservationDetail;
